import { LogIn } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useAuthGate } from "../../utils/useAuthGate";
import { useNavigate } from "react-router-dom";
import { useCategories } from "../../contexts/CategoryContext";
import { useSubCategories } from "../../contexts/SubCategoryContext";
import { useChat } from "../../contexts/ChatContext";
import HealJaiTalkButton from "../common/HealJaiTalkButton";
import WorldwideSection from "./WorldwideSection";
import TrustSection from "./TrustSection";
import PremiumCard from "./PremiumCard";
import HeroSection from "./HeroSection";

// Regions whose Compatibility subcategory has a dedicated in-chat UI
// instead of the generic Energy Match form
const REGIONS_WITH_DEDICATED_COMPATIBILITY_PAGE = new Set([
  "Astria Japan",
  "Astria Korea",
  "Astria GCC",
  "Astria Indonesia",
]);

// Maps the user's region value to the matching Astria category name in DB
const REGION_TO_ASTRIA_CATEGORY: Record<string, string> = {
  indonesia: "Astria Indonesia",
  canada: "Astria Canada",
  uk: "Astria UK",
  gcc: "Astria GCC",
  malaysia: "Astria Malaysia",
  singapore: "Astria Singapore",
  philippines: "Astria Philippines",
  vietnam: "Astria Vietnam V2",
  maxico: "Astria Mexico V2",
  brazil: "Astria Brazil",
  korea: "Astria Korea",
  india: "Astria India",
  japan: "Astria Japan",
  spanish: "Astria Spanish",
  us: "Astria US",
};

const SIDEBAR_COLORS = {
  amethyst: "#6D4C92",
  darkAmethyst: "#2D1B44",
  roseGold: "#E5B8AD",
  pearl: "#F8F4FF",
};

type Tab = "cosmic" | "heal";

const Sanctuary = () => {
  const { t } = useTranslation() as any;
  const [activeTab, setActiveTab] = useState<Tab>("heal");
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const { user } = useAuth();
  const { requireAuth } = useAuthGate();
  const navigate = useNavigate();
  const { getSubCategoriesByCategory } = useSubCategories();
  const { startNewChat, setCurrentChat } = useChat();
  const tabsRef = React.useRef<HTMLDivElement | null>(null);

  const handleExploreChart = () => {
    tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const PREMIUM_MESSAGES = [
    {
      title: "For new beginnings",
      text: t("sanctuary.premiumMessage.text1"),
    },
    {
      title: "For letting go",
      text: t("sanctuary.premiumMessage.text2"),
    },
    {
      title: "For confidence",
      text: t("sanctuary.premiumMessage.text3"),
    },
    {
      title: "For perseverance",
      text: t("sanctuary.premiumMessage.text4"),
    },
    {
      title: "For self-care",
      text: t("sanctuary.premiumMessage.text5"),
    },
    {
      title: "For inspiration",
      text: t("sanctuary.premiumMessage.text6"),
    },
    {
      title: "For small joys",
      text: t("sanctuary.premiumMessage.text7"),
    },
    {
      title: "For moving forward",
      text: t("sanctuary.premiumMessage.text8"),
    },
    {
      title: "For calmness",
      text: t("sanctuary.premiumMessage.text9"),
    },
    {
      title: "For self-worth",
      text: t("sanctuary.premiumMessage.text10"),
    },
  ];

  const [randomMessage] = useState(
    () => PREMIUM_MESSAGES[Math.floor(Math.random() * PREMIUM_MESSAGES.length)],
  );
  const {
    categories,
    isLoading: categoriesLoading,
    hasFetched: categoriesFetched,
  } = useCategories();

  const handleCompanionTalkCta = requireAuth(async () => {
    const talkCategory =
      categories.find(
        (c) => c.name === "HealJai Talk" || c.name === "Astria Talk",
      ) || categories.find((c) => c.name === "Companion Talk");

    if (!talkCategory) return;

    const newChat = await startNewChat(talkCategory.id);
    setCurrentChat(newChat);
    navigate(`/sanctuary?chat=${newChat.id}`);
  });

  const cardClass =
    "w-full rounded-[26px] px-6 py-7 bg-white border border-[#E8E3DA] " +
    "shadow-[0_8px_25px_rgba(0,0,0,0.06)] transition-all duration-300 " +
    "hover:-translate-y-1 hover:shadow-[0_15px_35px_rgba(0,0,0,0.10)]";

  const jarContent = useMemo(() => {
    return {
      horoscope: {
        title: t("sanctuary.jar.horoscope.title"),
        headline: t("sanctuary.jar.horoscope.headline"),
        sub: t("sanctuary.jar.horoscope.sub"),
        button: t("sanctuary.jar.horoscope.button"),
      },
      energy: {
        title: t("sanctuary.jar.energy.title"),
        headline: t("sanctuary.jar.energy.headline"),
        sub: t("sanctuary.jar.energy.sub"),
        button: t("sanctuary.jar.energy.button"),
      },
      intuition: {
        title: t("sanctuary.jar.intuition.title"),
        headline: t("sanctuary.jar.intuition.headline"),
        sub: t("sanctuary.jar.intuition.sub"),
        button: t("sanctuary.jar.intuition.button"),
      },
      alignment: {
        title: t("sanctuary.jar.alignment.title"),
        headline: t("sanctuary.jar.alignment.headline"),
        sub: t("sanctuary.jar.alignment.sub"),
        button: t("sanctuary.jar.alignment.button"),
      },
      innerCare: {
        title: t("sanctuary.jar.innerCare.title"),
        headline: t("sanctuary.jar.innerCare.headline"),
        sub: t("sanctuary.jar.innerCare.sub"),
        button: t("sanctuary.jar.innerCare.button"),
      },
      lifeMatters: {
        title: t("sanctuary.jar.lifeMatters.title"),
        headline: t("sanctuary.jar.lifeMatters.headline"),
        sub: t("sanctuary.jar.lifeMatters.sub"),
        button: t("sanctuary.jar.lifeMatters.button"),
      },
      selfWorth: {
        title: t("sanctuary.jar.selfWorth.title"),
        headline: t("sanctuary.jar.selfWorth.headline"),
        sub: t("sanctuary.jar.selfWorth.sub"),
        button: t("sanctuary.jar.selfWorth.button"),
      },
      emotionalBalance: {
        title: t("sanctuary.jar.emotionalBalance.title"),
        headline: t("sanctuary.jar.emotionalBalance.headline"),
        sub: t("sanctuary.jar.emotionalBalance.sub"),
        button: t("sanctuary.jar.emotionalBalance.button"),
      },
    } as Record<
      string,
      { title: string; headline: string; sub: string; button: string }
    >;
  }, [t]);

  const currentJar = selectedCard ? jarContent[selectedCard] : null;

  const userRegion = user?.region || "healjai";
  const isThailand = userRegion === "healjai";
  const isAllRegions =
    userRegion === "all" && (user?.allRegionsApproved || false);

  // HealJai Talk button (Thailand only)
  const healJaiTalkCategory = categories.filter(
    (c) => c.name === "HealJai Talk" || c.name === "Astria Talk",
  );

  // Astria Talk button (non-Thailand regions)
  const astriaTalkCategoryName = REGION_TO_ASTRIA_CATEGORY[userRegion] || "";
  const astriaTalkCategory = categories.filter(
    (c) => c.name === "Companion Talk",
  );

  // Energy Match tab — only for Thailand (and "all regions" if approved)
  const energyMatchCategory = categories.filter(
    (c) => c.name === "Energy Match",
  );
  const showEnergyMatchTab = isThailand || isAllRegions;

  // For non-Thailand regions, navigate to the Compatibility experience.
  // Regions with a dedicated in-chat Compatibility UI (Japan/Korea/GCC/Indonesia)
  // start a chat on that subcategory instead of opening the generic Energy Match form.
  const handleCompatibilityClick = async () => {
    if (!astriaTalkCategoryName) {
      navigate("/sanctuary");
      return;
    }

    const cat = categories.find((c) => c.name === astriaTalkCategoryName);
    if (!cat) {
      navigate("/sanctuary");
      return;
    }

    if (REGIONS_WITH_DEDICATED_COMPATIBILITY_PAGE.has(astriaTalkCategoryName)) {
      const catSubCategories = await getSubCategoriesByCategory(cat.id);
      const compatibilitySubCategory = catSubCategories.find((sc) =>
        sc.name.toLowerCase().includes("compatibility"),
      );

      if (compatibilitySubCategory) {
        const newChat = await startNewChat(cat.id, compatibilitySubCategory.id);
        setCurrentChat(newChat);
        navigate(`/sanctuary?chat=${newChat.id}`);
        return;
      }
    }

    navigate(
      `/energyMatch?country=${encodeURIComponent(astriaTalkCategoryName)}&categoryId=${cat.id}`,
    );
  };

  return (
    <div className="min-h-screen w-full font-sans bg-[#f0f2f1]">
      {selectedCard && currentJar && (
        <div className="fixed inset-0 z-50 bg-[#f0f2f1] flex flex-col md:ml-[320px]">
          <div className="h-14 flex items-center px-4 border-b border-[#E5B8AD]/30 flex-shrink-0">
            <button
              onClick={() => setSelectedCard(null)}
              className="text-2xl text-[#2D1B44] hover:opacity-70 transition"
              title={t("sanctuary.buttons.back")}
              aria-label={t("sanctuary.buttons.back")}
            >
              ←
            </button>

            <div className="flex-1 text-center pr-8">
              <div className="text-lg font-semibold text-[#2D1B44]">
                {currentJar.title}
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 pb-20">
            <div className="flex flex-col items-center text-center max-w-md mx-auto pt-8">
              <img
                src="/jar.png"
                alt={t("sanctuary.alt.heartJar")}
                className="w-40 h-40 sm:w-44 sm:h-44 object-contain mb-8"
                draggable={false}
              />

              <h1 className="text-3xl sm:text-5xl font-bold text-[#2D1B44] leading-tight">
                {currentJar.headline}
              </h1>

              <p className="mt-4 text-[#2D1B44]/70 text-base sm:text-lg">
                {currentJar.sub}
              </p>

              <button
                className="
                  mt-10
                  px-10 py-4 rounded-full
                  bg-[#F6EBDD]
                  text-[#2D1B44]
                  font-semibold
                  shadow-[0_12px_30px_rgba(0,0,0,0.10)]
                  hover:opacity-90 transition
                "
              >
                {currentJar.button}
              </button>
            </div>
          </div>
        </div>
      )}

      {!selectedCard && (
        <div className="w-full">
          {!user && (
            <div className="w-full px-6 py-6 flex justify-end">
              <button
                onClick={() => (window.location.href = "/login")}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-white hover:opacity-90 transition"
                style={{ backgroundColor: SIDEBAR_COLORS.amethyst }}
                type="button"
              >
                <LogIn className="w-4 h-4" />
                {t("sanctuary.buttons.login")}
              </button>
            </div>
          )}

          <div className="mx-auto w-full max-w-[1100px] px-4 sm:px-6 lg:px-10 pt-1 pb-20 bg-[#F1F1F1]">
            {user && (
              <div className="w-full flex justify-center mt-2 mb-5 sm:mb-7">
                <div className="w-full max-w-[560px] relative">
                  <div
                    className="absolute -inset-0.5 rounded-[24px] opacity-30 blur-2xl"
                    style={{
                      background:
                        "linear-gradient(140deg, rgba(109,76,146,0.35), rgba(229,184,173,0.32))",
                    }}
                  />
                  <motion.div
                    className="relative rounded-[22px] px-5 sm:px-6 py-5 sm:py-6 shadow-[0_14px_32px_rgba(0,0,0,0.16)]"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(246,235,221,0.92) 100%)",
                      border: "1px solid rgba(109,76,146,0.18)",
                      fontFamily:
                        '"Nunito","Quicksand","Varela Round","Segoe UI","Helvetica Neue",sans-serif',
                    }}
                    initial={{ opacity: 0, y: 12, scale: 0.985 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{
                      duration: 0.9,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  >
                    <motion.div className="flex items-center justify-between gap-4">
                      <span className="text-[11px] sm:text-xs uppercase tracking-[0.28em] text-[#6D4C92]">
                        {t("sanctuary.premiumMessage.title")}
                        {/* Today’s Reflection */}
                      </span>
                    </motion.div>
                    <p className="mt-3 text-[15px] italic sm:text-[17px] leading-relaxed text-[#2D1B44] font-semibold">
                      “{randomMessage.text}”
                    </p>
                    <div className="mt-3 text-sm text-[#6D4C92]/75 text-right">
                      — Astria
                    </div>
                  </motion.div>
                </div>
              </div>
            )}

            {!user && (
              <HeroSection
                onExploreChart={handleExploreChart}
                onCompanionTalk={handleCompanionTalkCta}
              />
            )}


            {user && (
              <div className="w-full flex justify-center mt-5 mb-4 sm:mb-6 relative">
                <motion.div
                  className="absolute w-[240px] sm:w-[300px] md:w-[360px] aspect-square rounded-full"
                  style={{
                    background: `
                    radial-gradient(circle,
                      rgba(255, 188, 220, 0.42) 0%,
                      rgba(196, 150, 255, 0.36) 28%,
                      rgba(120, 175, 255, 0.30) 52%,
                      rgba(129, 240, 255, 0.22) 68%,
                      rgba(255, 210, 170, 0.16) 82%,
                      transparent 100%)
                  `,
                    filter: "blur(42px)",
                  }}
                  animate={{
                    scale: [0.98, 1.04, 0.98],
                    opacity: [0.72, 0.9, 0.72],
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />

                <motion.div
                  className="absolute w-[205px] sm:w-[255px] md:w-[310px] aspect-square rounded-full"
                  style={{
                    background: `
                    radial-gradient(circle,
                      rgba(255,255,255,0.22) 0%,
                      rgba(255,170,220,0.24) 22%,
                      rgba(165,120,255,0.22) 44%,
                      rgba(110,165,255,0.18) 62%,
                      transparent 82%)
                  `,
                    filter: "blur(24px)",
                  }}
                  animate={{
                    scale: [1, 1.03, 1],
                    opacity: [0.68, 0.82, 0.68],
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />

                <motion.img
                  src="/logo_2.png"
                  alt="Astria logo"
                  className="relative z-10 w-[200px] sm:w-[260px] md:w-[320px] h-auto object-contain"
                  animate={{
                    scale: [1, 1.035, 1],
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  style={{
                    transformOrigin: "center center",
                  }}
                />
              </div>
            )}

            <div ref={tabsRef} className="flex justify-center mb-8 mt-6 px-3">
              <div className="flex w-full max-w-[360px] sm:max-w-none bg-white border border-[#E5B8AD]/40 rounded-full shadow-lg overflow-hidden">
                <button
                  onClick={() => setActiveTab("heal")}
                  className={`flex-1 flex items-center justify-center
                    px-2 py-1.5
                    sm:px-4 sm:py-2
                    lg:px-6 lg:py-4
                    rounded-full
                    text-[11px] sm:text-sm lg:text-lg
                    font-medium lg:font-semibold
                    transition-all duration-300
                    ${
                      activeTab === "heal"
                        ? "bg-[#F6EBDD] text-purple-800 shadow-md"
                        : "text-[#2D1B44]"
                    }`}
                >
                  <span className="text-base sm:text-lg lg:text-2xl">♡</span>
                  <span className="truncate">{t("sanctuary.tabs.heal")}</span>
                </button>

                <button
                  onClick={() => setActiveTab("cosmic")}
                  className={`flex-1 flex items-center justify-center
                    px-2 py-1.5
                    sm:px-4 sm:py-2
                    lg:px-6 lg:py-4
                    rounded-full
                    text-[11px] sm:text-sm lg:text-lg
                    font-medium lg:font-semibold
                    transition-all duration-300
                    ${
                      activeTab === "cosmic"
                        ? "bg-[#F6EBDD] text-purple-800 shadow-md"
                        : "text-[#2D1B44]"
                    }`}
                >
                  <span className="text-base sm:text-lg lg:text-2xl">🌙</span>
                  <span className="truncate">Guidance</span>
                </button>

                {showEnergyMatchTab ? (
                  <button
                    onClick={requireAuth(() =>
                      navigate(
                        `/energyMatch?category=${energyMatchCategory[0]?.id}`,
                      ),
                    )}
                    className={`flex-1 flex items-center justify-center
                      px-2 py-1.5
                      sm:px-4 sm:py-2
                      lg:px-6 lg:py-4
                      rounded-full
                      text-[11px] sm:text-sm lg:text-lg
                      font-medium lg:font-semibold
                      transition-all duration-300 text-[#2D1B44]`}
                  >
                    <span className="text-base sm:text-lg lg:text-2xl">⚡</span>
                    <span className="truncate">Energy Match</span>
                  </button>
                ) : (
                  <button
                    onClick={requireAuth(handleCompatibilityClick)}
                    className={`flex-1 flex items-center justify-center
                      px-2 py-1.5
                      sm:px-4 sm:py-2
                      lg:px-6 lg:py-4
                      rounded-full
                      text-[11px] sm:text-sm lg:text-lg
                      font-medium lg:font-semibold
                      transition-all duration-300 text-[#2D1B44]`}
                  >
                    <span className="text-base sm:text-lg lg:text-2xl">💞</span>
                    <span className="truncate">Compatibility</span>
                  </button>
                )}
              </div>
            </div>

            {activeTab === "cosmic" && (
              <div className="flex flex-col items-center">
                <div className="w-full max-w-[700px] mx-auto rounded-[28px] p-6 sm:p-7 mb-8 bg-white shadow-[0_10px_30px_rgba(0,0,0,0.12)]">
                  <p className="text-center text-base sm:text-lg leading-relaxed font-medium text-[#4A3F35]">
                    {t("sanctuary.cosmic.quote.line1")}
                    <br />
                    {t("sanctuary.cosmic.quote.line2")}
                  </p>
                  <p className="text-center text-sm text-[#8A7F73] mt-3 italic">
                    {t("sanctuary.cosmic.quote.author")}
                  </p>
                </div>

                <div className="w-full max-w-[900px]">
                  <div className="grid grid-cols-2 gap-6">
                    <div
                      className={`${cardClass} cursor-pointer`}
                      onClick={() => setSelectedCard("horoscope")}
                    >
                      <h3 className="font-semibold mb-3 text-[#2D1B44] sm:text-base">
                        {t("sanctuary.cosmic.cards.horoscope.title")}
                      </h3>
                      <ul className="text-[12px] sm:text-sm space-y-1.5 sm:space-y-2 text-[#4A4A4A]">
                        <li>{t("sanctuary.cosmic.cards.horoscope.point1")}</li>
                        <li>{t("sanctuary.cosmic.cards.horoscope.point2")}</li>
                      </ul>
                    </div>

                    <div
                      className={`${cardClass} cursor-pointer`}
                      onClick={() => setSelectedCard("energy")}
                    >
                      <h3 className="font-semibold mb-3 text-[#2D1B44] sm:text-base">
                        {t("sanctuary.cosmic.cards.energy.title")}
                      </h3>
                      <ul className="text-[12px] sm:text-sm space-y-1.5 sm:space-y-2 text-[#4A4A4A]">
                        <li>{t("sanctuary.cosmic.cards.energy.point1")}</li>
                        <li>{t("sanctuary.cosmic.cards.energy.point2")}</li>
                      </ul>
                    </div>

                    <div
                      className={`${cardClass} cursor-pointer`}
                      onClick={() => setSelectedCard("intuition")}
                    >
                      <h3 className="font-semibold mb-3 text-[#2D1B44] sm:text-base">
                        {t("sanctuary.cosmic.cards.intuition.title")}
                      </h3>
                      <ul className="text-[12px] sm:text-sm space-y-1.5 sm:space-y-2 text-[#4A4A4A]">
                        <li>{t("sanctuary.cosmic.cards.intuition.point1")}</li>
                        <li>{t("sanctuary.cosmic.cards.intuition.point2")}</li>
                      </ul>
                    </div>

                    <div
                      className={`${cardClass} cursor-pointer`}
                      onClick={() => setSelectedCard("alignment")}
                    >
                      <h3 className="font-semibold mb-3 text-[#2D1B44] sm:text-base">
                        {t("sanctuary.cosmic.cards.alignment.title")}
                      </h3>
                      <ul className="text-[12px] sm:text-sm space-y-1.5 sm:space-y-2 text-[#4A4A4A]">
                        <li>{t("sanctuary.cosmic.cards.alignment.point1")}</li>
                        <li>{t("sanctuary.cosmic.cards.alignment.point2")}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "heal" && (
              <div className="flex flex-col items-center">
                <div className="w-full max-w-[700px] mx-auto rounded-[28px] p-6 sm:p-7 mb-8 bg-white shadow-[0_10px_30px_rgba(0,0,0,0.12)]">
                  <p className="text-center text-base sm:text-lg leading-relaxed font-medium text-[#4A3F35]">
                    {t("sanctuary.heal.quote.line1")}
                    <br />
                    {t("sanctuary.heal.quote.line2")}
                  </p>
                  <p className="text-center text-sm text-[#8A7F73] mt-3 italic">
                    {t("sanctuary.heal.quote.author")}
                  </p>
                </div>

                <div className="w-full max-w-[900px]">
                  <div className="grid grid-cols-2 gap-6">
                    <div
                      className={`${cardClass} cursor-pointer`}
                      onClick={() => setSelectedCard("innerCare")}
                    >
                      <h3 className="font-semibold mb-3 text-[#2D1B44] sm:text-base flex items-center gap-2">
                        <span className="text-xl leading-none">♡</span>
                        <span>{t("sanctuary.heal.cards.innerCare.title")}</span>
                      </h3>
                      <ul className="text-[12px] sm:text-sm space-y-1.5 sm:space-y-2 text-[#4A4A4A]">
                        <li>{t("sanctuary.heal.cards.innerCare.point1")}</li>
                        <li>{t("sanctuary.heal.cards.innerCare.point2")}</li>
                      </ul>
                    </div>

                    <div
                      className={`${cardClass} cursor-pointer`}
                      onClick={() => setSelectedCard("lifeMatters")}
                    >
                      <h3 className="font-semibold mb-3 text-[#2D1B44] sm:text-base">
                        {t("sanctuary.heal.cards.lifeMatters.title")}
                      </h3>
                      <ul className="text-[12px] sm:text-sm space-y-1.5 sm:space-y-2 text-[#4A4A4A]">
                        <li>{t("sanctuary.heal.cards.lifeMatters.point1")}</li>
                        <li>{t("sanctuary.heal.cards.lifeMatters.point2")}</li>
                      </ul>
                    </div>

                    <div
                      className={`${cardClass} cursor-pointer`}
                      onClick={() => setSelectedCard("selfWorth")}
                    >
                      <h3 className="font-semibold mb-3 text-[#2D1B44] sm:text-base">
                        {t("sanctuary.heal.cards.selfWorth.title")}
                      </h3>
                      <ul className="text-[12px] sm:text-sm space-y-1.5 sm:space-y-2 text-[#4A4A4A]">
                        <li>{t("sanctuary.heal.cards.selfWorth.point1")}</li>
                        <li>{t("sanctuary.heal.cards.selfWorth.point2")}</li>
                      </ul>
                    </div>

                    <div
                      className={`${cardClass} cursor-pointer`}
                      onClick={() => setSelectedCard("emotionalBalance")}
                    >
                      <h3 className="font-semibold mb-3 text-[#2D1B44] sm:text-base">
                        {t("sanctuary.heal.cards.emotionalBalance.title")}
                      </h3>
                      <ul className="text-[12px] sm:text-sm space-y-1.5 sm:space-y-2 text-[#4A4A4A]">
                        <li>
                          {t("sanctuary.heal.cards.emotionalBalance.point1")}
                        </li>
                        <li>
                          {t("sanctuary.heal.cards.emotionalBalance.point2")}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {<WorldwideSection />}
            <PremiumCard />
            {<TrustSection />}
          </div>
        </div>
      )}

      {/* Floating Talk button: HealJai Talk for Thailand, Astria Talk for other regions */}
      {!categoriesFetched && categoriesLoading && (
        <div
          className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-12 h-12 rounded-full shadow-[0_14px_32px_rgba(0,0,0,0.25)]"
          style={{ backgroundColor: SIDEBAR_COLORS.amethyst }}
        >
          <div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
        </div>
      )}
      {isThailand || isAllRegions
        ? healJaiTalkCategory.map((cat) => (
            <HealJaiTalkButton
              key={cat.id}
              category={cat}
              label={t("astriaHome.talk.astria")}
            />
          ))
        : astriaTalkCategory.map((cat) => (
            <HealJaiTalkButton
              key={cat.id}
              category={cat}
              label={t("astriaHome.talk.companion")}
            />
          ))}
    </div>
  );
};

export default Sanctuary;
